import { useEffect, useState } from 'react';
import NextLink from 'next/link';
import { Form, Formik } from 'formik';
import { bool, object, string } from 'yup';

import CloseCircle from '@/icons/duotone/close-circle.svg';
import TickCircle from '@/icons/duotone/tick-circle.svg';
import { Button, Link, Textarea, TextInput } from '@/atoms';
import { ButtonSize, ButtonVariant } from '@/atoms/button/types';
import Checkbox from '@/atoms/checkbox';
import InternationalPhoneInput from '@/atoms/international-phone-input';
import { isPhoneValid } from '@/atoms/international-phone-input/phone-validation';

import { routes } from '@/lib';
import { post } from '@/utils/client';
import { gtmPush, hash256 } from '@/utils/gtm';

import type { FunctionComponent } from 'react';
import type { ParsedCountry } from 'react-international-phone';
import type { TypeContactFormProps } from './types';

export const ContactForm: FunctionComponent<TypeContactFormProps> = ({
  flat,
  FormStyle = 'default',
  destinatario,
}: TypeContactFormProps) => {
  const registerSchema = object().shape({
    name: string()
      .required('El nombre es obligatorio')
      .min(2, 'El campo es demasiado corto')
      .max(50, 'El campo es demasiado largo'),
    telephone: string().test(
      'test-valid-phone',
      'El teléfono no es válido',
      () => isPhoneValid(formValues.telephone),
    ),
    email: string()
      .email('Formato de email es incorrecto')
      .required('El email es obligatorio'),
    message: string()
      .min(2, 'El campo es demasiado corto')
      .max(500, 'El campo es demasiado largo')
      .required('El email es obligatorio'),
    privacy: bool().oneOf([true], 'Debes aceptar las políticas de privacidad'),
  });
  const defaultValues = {
    name: '',
    telephone: '',
    formattedTelephone: '',
    email: '',
    message: '',
    privacy: false,
    comercial: false,
  };
  const [formValues, setFormValues] = useState(defaultValues);
  const [errorWhenSend, setErrorWhenSend] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmit, setIsSubmit] = useState(false);

  useEffect(() => {
    if (isSubmit) {
      const hashedEmail = hash256(formValues.email);
      const hashedPhone = hash256(formValues.telephone);
      const hashedFirstName = hash256(formValues.name.split(' ')[0]);
      const hashedLastName = hash256(formValues.name.split(' ')[1]);

      Promise.all([
        hashedEmail,
        hashedPhone,
        hashedFirstName,
        hashedLastName,
      ]).then(([email, phone, firstName, lastName]) => {
        gtmPush({
          event: 'form_submit',
          form_name: 'Contacto',
          form_destination: document.URL,
          form_submit_text: 'Contactar',
          city_area: flat?.cityArea,
          Email: email,
          Phone: phone,
          FirstName: firstName,
          LastName: lastName,
        });
      });
    }
  }, [isSubmit]);

  return (
    <>
      {errorWhenSend && isSubmit && (
        <div className="rounded-custom flex items-center gap-2 bg-[#FFDCDE] px-4 pt-4 pb-2">
          <CloseCircle width={64} height={64} />
          {errorMessage || 'Ha ocurrido un error'}
          {!errorMessage && (
            <Link
              as="button"
              onClick={() => {
                setErrorWhenSend(false);
                setErrorMessage('');
                setIsSubmit(false);
              }}
              className="sm:shrink-0"
            >
              Intentarlo de nuevo
            </Link>
          )}
        </div>
      )}

      {!errorWhenSend && isSubmit && (
        <div className="rounded-custom flex items-center gap-2 px-4 pt-4 pb-2">
          <TickCircle width={64} height={64} />
          Tu mensaje ha sido enviado exitosamente.
        </div>
      )}

      {!isSubmit && (
        <Formik
          onSubmit={async (data) => {
            data.telephone = formValues.telephone;
            data.formattedTelephone = formValues.formattedTelephone;
            setFormValues(data);
            await fetch('https://api.ipify.org?format=json')
              .then((res) => res.json())
              .then((res) => {
                post('/api/email', {
                  data,
                  flat: {
                    reference: flat?.codigoUnidadAlquiler ?? '',
                    // Category of the apartment. If the type is "Viviendas de alquiler", then "Alquiler". If the type is "Viviendas de venta", then "Venta"
                    type:
                      flat?.ventaAlquiler === undefined
                        ? ''
                        : flat?.ventaAlquiler,
                  },
                  url: document.URL,
                  ip: res.ip,
                  destinatario,
                })
                  .then((res) => {
                    if (res.message !== 'OK' || res.status !== 'OK')
                      setErrorWhenSend(false);
                  })
                  .catch((error) => {
                    if (error?.errors?.[0]?.reason === 'rate-limit') {
                      setErrorMessage(error.errors[0].message);
                    }
                    setErrorWhenSend(true);
                  })
                  .finally(() => setIsSubmit(true));
              });
          }}
          validationSchema={registerSchema}
          initialValues={formValues}
        >
          {({ isSubmitting }) => (
            <Form className="flex flex-col gap-4">
              <TextInput
                label={`${FormStyle === 'boxed' ? '' : 'Nombre'}`}
                name="name"
                placeholder="Nombre"
                className={`${
                  FormStyle === 'boxed' &&
                  'border-neutrals-200 bg-primary-600 text-neutrals-200 rounded-none border-0 border-b focus:ring-0'
                }`}
              />
              <InternationalPhoneInput
                label={`${FormStyle === 'boxed' ? '' : 'Teléfono'}`}
                name="telephone"
                onChange={(
                  value: string,
                  meta: { country: ParsedCountry; inputValue: string },
                ) => {
                  const formattedPhone = `(${meta.country?.iso2?.toUpperCase()}) +${
                    meta.country.dialCode
                  }-${value.replace(`+${meta.country.dialCode}`, '')}`;
                  setFormValues({
                    ...formValues,
                    telephone: value,
                    formattedTelephone: formattedPhone,
                  });
                }}
                inputClassName={`${
                  FormStyle === 'boxed'
                    ? 'bg-primary-600! border-none! border-b focus:ring-0 rounded-none text-neutrals-200!'
                    : 'border-transparent!'
                }`}
                className={`${
                  FormStyle === 'boxed'
                    ? 'border-b-neutrals-200! bg-primary-600! text-neutrals-200! rounded-none! border-b-2! border-transparent! focus:border-transparent! focus:ring-0!'
                    : 'border-neutrals-500! bg-neutrals-0! text-neutrals-500 rounded border!'
                }`}
              />

              <TextInput
                label={`${FormStyle === 'boxed' ? '' : 'Email'}`}
                name="email"
                placeholder="ejemplo@mail.com"
                type="email"
                className={`${
                  FormStyle === 'boxed' &&
                  'border-neutrals-200 bg-primary-600 text-neutrals-200 rounded-none border-0 border-b focus:ring-0'
                }`}
              />

              <Textarea
                label={`${FormStyle === 'boxed' ? '' : 'Mensaje'}`}
                name="message"
                placeholder="Añadir mensaje"
                className={`${
                  FormStyle === 'boxed' &&
                  'border-neutrals-200 bg-primary-600 text-neutrals-200 rounded-none border-0 border-b focus:ring-0'
                }`}
              />

              <Checkbox
                name="privacy"
                className={`${FormStyle === 'boxed' && 'text-neutrals-0!'}`}
              >
                Acepto la&nbsp;
                <NextLink
                  href={routes.privacy}
                  prefetch={false}
                  target="_blank"
                  className={`${
                    FormStyle === 'boxed' && 'text-secondary-500!'
                  }`}
                >
                  política de privacidad
                </NextLink>
              </Checkbox>

              <Checkbox
                name="comercial"
                className={`${FormStyle === 'boxed' && 'text-neutrals-0!'}`}
              >
                Acepto recibir comunicaciones comerciales
              </Checkbox>

              <hr className="text-neutrals-100 mt-2 h-px" />

              <Button
                type="submit"
                variant={ButtonVariant.SECONDARY}
                size={ButtonSize.MEDIUM}
                className="md:w-full"
                disabled={isSubmitting}
                loading={isSubmitting}
              >
                Contactar
              </Button>
            </Form>
          )}
        </Formik>
      )}
    </>
  );
};

ContactForm.displayName = 'ContactForm';
